// src/services/issueService.js
import { authenticatedFetch } from './api';
import { config } from '../config';
import logger from '../utils/debugLogger.js';

const SERVICE_NAME = '[IssueService]';

const DEBUG_LEVELS = {
    INFO: 'INFO',
    WARN: 'WARN',
    ERROR: 'ERROR',
    DEBUG: 'DEBUG'
};

const logService = (level, operation, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `${timestamp} ${SERVICE_NAME}[${operation}] ${message}`;
    
    const logData = {
        operation,
        message,
        data,
        timestamp
    };

    switch (level) {
        case DEBUG_LEVELS.ERROR:
            logger.error(logMessage, logData);
            break;
        case DEBUG_LEVELS.WARN:
            logger.warn(logMessage, logData);
            break;
        case DEBUG_LEVELS.DEBUG:
            logger.debug(logMessage, logData);
            break;
        default:
            logger.log(logMessage, logData);
    }
};

const handleServiceError = (operation, error, context = {}) => {
    const errorDetails = {
        operation,
        message: error.message,
        status: error.status || error.statusCode,
        context,
        stack: error.stack,
        timestamp: new Date().toISOString()
    };

    logService(DEBUG_LEVELS.ERROR, operation, 'Service error occurred', errorDetails);
    
    const enhancedError = new Error(error.message || 'An unexpected error occurred');
    enhancedError.operation = operation;
    enhancedError.context = context;
    enhancedError.originalError = error;
    
    throw enhancedError;
};

const retryRequest = async (fn, maxRetries = 3, delay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            if (attempt === maxRetries) throw error;
            
            logService(DEBUG_LEVELS.WARN, 'RETRY', `Attempt ${attempt} failed, retrying in ${delay}ms`, {
                attempt,
                maxRetries,
                error: error.message
            });
            
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
    }
};

const validatePagination = (options) => {
    const { limit, offset } = options;
    
    if (limit !== undefined && (limit < 1 || limit > 100)) {
        throw new Error('Limit must be between 1 and 100');
    }
    
    if (offset !== undefined && offset < 0) {
        throw new Error('Offset must be non-negative');
    }
};

const validateCoordinates = (lat, lng) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    
    if (isNaN(latitude) || latitude < -90 || latitude > 90) {
        throw new Error('Invalid latitude: must be between -90 and 90');
    }
    
    if (isNaN(longitude) || longitude < -180 || longitude > 180) {
        throw new Error('Invalid longitude: must be between -180 and 180');
    }
    
    return { latitude, longitude };
};

export const fetchAllIssues = async (options = {}) => {
    const operation = 'FETCH_ALL';
    const startTime = Date.now();
    
    logService(DEBUG_LEVELS.DEBUG, operation, 'Initiating fetch all issues request', { options });

    try {
        validatePagination(options);
        
        const { filters = {}, limit, offset, sortBy, sortDirection } = options;
        const queryParams = new URLSearchParams();

        if (filters.status) queryParams.append('status', filters.status);
        if (filters.issue_type) queryParams.append('issue_type', filters.issue_type);
        if (filters.reported_by_user_id) queryParams.append('reported_by_user_id', filters.reported_by_user_id);
        if (filters.work_order_id) queryParams.append('work_order_id', filters.work_order_id);
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.severity) queryParams.append('severity', filters.severity);

        if (limit !== undefined) queryParams.append('limit', limit.toString());
        if (offset !== undefined) queryParams.append('offset', offset.toString());
        if (sortBy) queryParams.append('sortBy', sortBy);
        if (sortDirection) queryParams.append('sortDirection', sortDirection);

        const endpoint = `/issues${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        
        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending request to endpoint', { endpoint });

        const responseData = await retryRequest(
            () => authenticatedFetch(endpoint),
            3,
            1000
        );
        
        if (!responseData || typeof responseData.totalCount !== 'number' || !Array.isArray(responseData.data)) {
            throw new Error('Invalid response format from server');
        }

        const executionTime = Date.now() - startTime;
        
        logService(DEBUG_LEVELS.INFO, operation, 'Successfully fetched issues', {
            count: responseData.data.length,
            totalCount: responseData.totalCount,
            executionTime: `${executionTime}ms`,
            cached: responseData.cached || false
        });

        return {
            ...responseData,
            metadata: {
                executionTime,
                cached: responseData.cached || false,
                timestamp: new Date().toISOString()
            }
        };

    } catch (error) {
        handleServiceError(operation, error, { options });
    }
};

export const fetchIssueLocations = async (filters = {}) => {
    const operation = 'FETCH_LOCATIONS';
    const startTime = Date.now();
    
    logService(DEBUG_LEVELS.DEBUG, operation, 'Fetching issue locations', { filters });

    try {
        const queryParams = new URLSearchParams();
        
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.issue_type) queryParams.append('issue_type', filters.issue_type);
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.includeMetadata) queryParams.append('includeMetadata', 'true');
        
        const endpoint = `/issues/locations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        
        logService(DEBUG_LEVELS.DEBUG, operation, 'Requesting locations', { endpoint });

        const responseData = await retryRequest(
            () => authenticatedFetch(endpoint),
            3,
            1000
        );

        const issueLocations = Array.isArray(responseData.data) ? responseData.data : 
                              Array.isArray(responseData) ? responseData : [];

        if (!Array.isArray(issueLocations)) {
            throw new Error('Invalid data format for issue locations');
        }

        const validLocations = issueLocations.filter(l => 
            l && typeof l.lat === 'number' && typeof l.lng === 'number' &&
            !isNaN(l.lat) && !isNaN(l.lng)
        );

        if (validLocations.length !== issueLocations.length) {
            logService(DEBUG_LEVELS.WARN, operation, 'Filtered invalid locations', {
                total: issueLocations.length,
                valid: validLocations.length,
                filtered: issueLocations.length - validLocations.length
            });
        }

        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Successfully fetched locations', {
            count: validLocations.length,
            executionTime: `${executionTime}ms`
        });

        return validLocations;

    } catch (error) {
        handleServiceError(operation, error, { filters });
    }
};

export const resolveIssue = async (issueId, resolutionDetails = {}) => {
    const operation = 'RESOLVE';
    const startTime = Date.now();
    
    logService(DEBUG_LEVELS.INFO, operation, 'Initiating issue resolution', { 
        issueId, 
        hasNotes: !!resolutionDetails.resolution_notes 
    });

    try {
        if (!issueId) {
            throw new Error('Issue ID is required to resolve an issue');
        }

        const endpoint = `/issues/${issueId}/resolve`;
        
        const options = {
            method: 'PUT',
            body: JSON.stringify(resolutionDetails),
        };

        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending resolution request', { 
            endpoint, 
            issueId 
        });

        const responseData = await authenticatedFetch(endpoint, options);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Issue resolved successfully', {
            issueId,
            executionTime: `${executionTime}ms`,
            response: responseData
        });

        return responseData;

    } catch (error) {
        handleServiceError(operation, error, { issueId, resolutionDetails });
    }
};

export const createIssue = async (issueData) => {
    const operation = 'CREATE';
    const startTime = Date.now();
    const transactionId = `CREATE_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logService(DEBUG_LEVELS.INFO, operation, 'Creating new issue', { 
        transactionId,
        isFormData: issueData instanceof FormData,
        hasImage: issueData instanceof FormData && issueData.has('issueImage')
    });

    try {
        const endpoint = '/issues';
        const isFormData = issueData instanceof FormData;

        if (isFormData) {
            const lat = issueData.get('latitude');
            const lng = issueData.get('longitude');
            
            if (lat && lng) {
                validateCoordinates(lat, lng);
            }
        } else if (issueData.latitude && issueData.longitude) {
            validateCoordinates(issueData.latitude, issueData.longitude);
        }

        const options = {
            method: 'POST',
            body: isFormData ? issueData : JSON.stringify(issueData),
        };

        if (!isFormData) {
            options.headers = { 'Content-Type': 'application/json' };
        }

        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending create request', { 
            endpoint, 
            transactionId 
        });

        const responseData = await authenticatedFetch(endpoint, options, isFormData);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Issue created successfully', {
            transactionId,
            issueId: responseData.issue?.id,
            executionTime: `${executionTime}ms`,
            potentialDuplicate: responseData.issue?.potential_duplicate
        });

        return {
            ...responseData,
            transactionId,
            executionTime
        };

    } catch (error) {
        handleServiceError(operation, error, { transactionId });
    }
};

export const createPublicIssue = async (formData) => {
    const operation = 'CREATE_PUBLIC';
    const startTime = Date.now();
    const transactionId = `PUBLIC_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logService(DEBUG_LEVELS.INFO, operation, 'Submitting public issue', { 
        transactionId,
        hasImage: formData.has('issueImage')
    });

    try {
        const lat = formData.get('latitude');
        const lng = formData.get('longitude');
        
        if (lat && lng) {
            validateCoordinates(lat, lng);
        }

        const endpoint = `${config.API_URL}/api/v1/public/issues`;
        
        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending public issue request', { 
            endpoint, 
            transactionId 
        });
        
        const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
        });

        const responseData = await response.json();

        if (!response.ok) {
            throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
        }

        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Public issue submitted successfully', {
            transactionId,
            issueId: responseData.issue?.id,
            executionTime: `${executionTime}ms`
        });

        return {
            ...responseData,
            transactionId,
            executionTime
        };

    } catch (error) {
        handleServiceError(operation, error, { transactionId });
    }
};

export const fetchIssueById = async (issueId) => {
    const operation = 'FETCH_BY_ID';
    const startTime = Date.now();
    
    logService(DEBUG_LEVELS.DEBUG, operation, 'Fetching issue by ID', { issueId });

    try {
        if (!issueId) {
            throw new Error('Issue ID is required');
        }

        const endpoint = `/issues/${issueId}`;
        
        logService(DEBUG_LEVELS.DEBUG, operation, 'Requesting issue details', { endpoint });

        const responseData = await authenticatedFetch(endpoint);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Issue fetched successfully', {
            issueId,
            status: responseData.status,
            executionTime: `${executionTime}ms`,
            cached: responseData.cached || false
        });

        return responseData.data || responseData;

    } catch (error) {
        handleServiceError(operation, error, { issueId });
    }
};

export const updateIssue = async (issueId, updateData) => {
    const operation = 'UPDATE';
    const startTime = Date.now();
    const updateId = `UPD_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logService(DEBUG_LEVELS.INFO, operation, 'Updating issue', { 
        updateId,
        issueId, 
        fields: Object.keys(updateData) 
    });

    try {
        if (!issueId) {
            throw new Error('Issue ID is required');
        }

        const endpoint = `/issues/${issueId}`;
        
        const options = {
            method: 'PUT',
            body: JSON.stringify(updateData),
        };

        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending update request', { 
            endpoint, 
            updateId 
        });

        const responseData = await authenticatedFetch(endpoint, options);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Issue updated successfully', {
            updateId,
            issueId,
            changes: responseData.changes,
            executionTime: `${executionTime}ms`
        });

        return responseData;

    } catch (error) {
        handleServiceError(operation, error, { updateId, issueId, updateData });
    }
};

export const deleteIssue = async (issueId) => {
    const operation = 'DELETE';
    const startTime = Date.now();
    const deleteId = `DEL_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logService(DEBUG_LEVELS.WARN, operation, 'Deleting issue', { deleteId, issueId });

    try {
        if (!issueId) {
            throw new Error('Issue ID is required');
        }

        const endpoint = `/issues/${issueId}`;
        
        const options = {
            method: 'DELETE',
        };

        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending delete request', { 
            endpoint, 
            deleteId 
        });

        const responseData = await authenticatedFetch(endpoint, options);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Issue deleted successfully', {
            deleteId,
            issueId,
            executionTime: `${executionTime}ms`
        });

        return responseData;

    } catch (error) {
        handleServiceError(operation, error, { deleteId, issueId });
    }
};

export const findNearbyIssues = async (lat, lng, radius = 100, timeWindow = 24) => {
    const operation = 'FIND_NEARBY';
    const startTime = Date.now();
    
    logService(DEBUG_LEVELS.DEBUG, operation, 'Finding nearby issues', { 
        lat, lng, radius, timeWindow 
    });

    try {
        validateCoordinates(lat, lng);

        const queryParams = new URLSearchParams({
            lat: lat.toString(),
            lng: lng.toString(),
            radius: radius.toString(),
            timeWindow: timeWindow.toString()
        });

        const endpoint = `/issues/find-nearby?${queryParams.toString()}`;
        
        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending nearby search request', { endpoint });

        const responseData = await retryRequest(
            () => authenticatedFetch(endpoint),
            2,
            500
        );

        const nearbyIssues = Array.isArray(responseData.data) ? responseData.data : 
                            Array.isArray(responseData) ? responseData : [];

        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Nearby issues found', {
            count: nearbyIssues.length,
            executionTime: `${executionTime}ms`,
            searchRadius: radius,
            timeWindow
        });

        return nearbyIssues;

    } catch (error) {
        handleServiceError(operation, error, { lat, lng, radius, timeWindow });
    }
};

export const linkIssue = async (issueId, linkData) => {
    const operation = 'LINK';
    const startTime = Date.now();
    const linkId = `LINK_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logService(DEBUG_LEVELS.INFO, operation, 'Linking report to existing issue', { 
        linkId,
        issueId, 
        hasDescription: !!linkData.description 
    });

    try {
        if (!issueId || !linkData || !linkData.description) {
            throw new Error('Issue ID and description are required to link a report');
        }

        const endpoint = `/issues/${issueId}/link`;
        
        const options = {
            method: 'PUT',
            body: JSON.stringify(linkData),
        };

        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending link request', { 
            endpoint, 
            linkId 
        });

        const responseData = await authenticatedFetch(endpoint, options);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Report linked successfully', {
            linkId,
            issueId,
            linkedReports: responseData.linkedReports,
            executionTime: `${executionTime}ms`
        });

        return responseData;

    } catch (error) {
        handleServiceError(operation, error, { linkId, issueId, linkData });
    }
};

export const getIssueStats = async () => {
    const operation = 'GET_STATS';
    const startTime = Date.now();
    
    logService(DEBUG_LEVELS.DEBUG, operation, 'Fetching issue statistics');

    try {
        const endpoint = '/issues/stats';
        
        logService(DEBUG_LEVELS.DEBUG, operation, 'Requesting statistics', { endpoint });

        const responseData = await authenticatedFetch(endpoint);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Statistics fetched successfully', {
            stats: responseData.data,
            executionTime: `${executionTime}ms`,
            cached: responseData.cached || false
        });

        return responseData.data || responseData;

    } catch (error) {
        handleServiceError(operation, error, {});
    }
};

export const bulkUpdateIssues = async (issueIds, updateData) => {
    const operation = 'BULK_UPDATE';
    const startTime = Date.now();
    const bulkId = `BULK_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logService(DEBUG_LEVELS.INFO, operation, 'Bulk updating issues', { 
        bulkId,
        count: issueIds.length, 
        fields: Object.keys(updateData) 
    });

    try {
        if (!Array.isArray(issueIds) || issueIds.length === 0) {
            throw new Error('Issue IDs array is required and must not be empty');
        }

        const endpoint = '/issues/bulk-update';
        
        const options = {
            method: 'PUT',
            body: JSON.stringify({ issueIds, updateData }),
        };

        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending bulk update request', { 
            endpoint, 
            bulkId 
        });

        const responseData = await authenticatedFetch(endpoint, options);
        
        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Bulk update completed', {
            bulkId,
            updatedCount: responseData.updatedCount,
            executionTime: `${executionTime}ms`
        });

        return responseData;

    } catch (error) {
        handleServiceError(operation, error, { bulkId, issueIds, updateData });
    }
};

export const searchIssues = async (searchTerm, filters = {}, limit = 20) => {
    const operation = 'SEARCH';
    const startTime = Date.now();
    
    logService(DEBUG_LEVELS.DEBUG, operation, 'Searching issues', { 
        searchTerm, 
        filters, 
        limit 
    });

    try {
        if (!searchTerm || searchTerm.trim() === '') {
            throw new Error('Search term is required');
        }

        const queryParams = new URLSearchParams({
            q: searchTerm,
            limit: limit.toString()
        });

        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                queryParams.append(key, filters[key]);
            }
        });

        const endpoint = `/issues/search?${queryParams.toString()}`;
        
        logService(DEBUG_LEVELS.DEBUG, operation, 'Sending search request', { endpoint });

        const responseData = await authenticatedFetch(endpoint);
        
        const searchResults = Array.isArray(responseData.data) ? responseData.data : 
                             Array.isArray(responseData) ? responseData : [];

        const executionTime = Date.now() - startTime;

        logService(DEBUG_LEVELS.INFO, operation, 'Search completed', {
            resultsCount: searchResults.length,
            executionTime: `${executionTime}ms`,
            searchTerm
        });

        return searchResults;

    } catch (error) {
        handleServiceError(operation, error, { searchTerm, filters });
    }
};

/**
 * 🚀 ENTERPRISE UPDATE: Fetch geographic distribution for Field Ops Zones
 * Fetches the distribution of active issues across field operations zones.
 * @returns {Promise<Array>} Array of zone distribution data
 */
export const fetchZoneDistribution = async () => {
    const operation = 'fetchZoneDistribution';
    logService(DEBUG_LEVELS.INFO, operation, 'Fetching issue zone distribution');
    
    try {
        const responseData = await authenticatedFetch('/issues/zones/distribution');
        
        const zones = Array.isArray(responseData?.data) ? responseData.data : 
                      Array.isArray(responseData) ? responseData : [];
                      
        logService(DEBUG_LEVELS.INFO, operation, 'Successfully fetched zone distribution', {
            zoneCount: zones.length
        });
        
        return zones;
    } catch (error) {
        handleServiceError(operation, error);
    }
};

/**
 * 🚀 ENTERPRISE UPDATE: Fetch Comprehensive Zone Analytics
 * Fetches all KPIs, Issues, Assets, and Work Orders bounded inside a specific field operation zone.
 * @param {number} zoneId - The ID of the assignment zone
 */
export const fetchZoneAnalytics = async (zoneId) => {
    const operation = 'fetchZoneAnalytics';
    logService(DEBUG_LEVELS.INFO, operation, `Fetching rich analytics for zone ${zoneId}`);
    
    try {
        const responseData = await authenticatedFetch(`/issues/zones/${zoneId}/analytics`);
        return responseData.data;
    } catch (error) {
        handleServiceError(operation, error);
    }
};

export default {
    fetchAllIssues,
    fetchIssueLocations,
    resolveIssue,
    createIssue,
    createPublicIssue,
    fetchIssueById,
    updateIssue,
    deleteIssue,
    findNearbyIssues,
    linkIssue,
    getIssueStats,
    bulkUpdateIssues,
    searchIssues,
    fetchZoneDistribution,
    fetchZoneAnalytics
};